import { NumericInput } from "@/components/shared/NumericInput";
import { useForm } from "@tanstack/react-form";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import {
  ctpPlateLogFormSchema,
  type CtpPlateLogFormData,
} from "@/shared/schemas";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useState } from "react";
import { joinFieldErrors } from "@/lib/form/FormFieldError";

import { toVnDateString } from "@apv-erp/status";
interface CtpPlateLogFormProps {
  defaultValues?: Partial<CtpPlateLogFormData>;
  onSubmit: (data: CtpPlateLogFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

function FieldError({ field }: { field: any }) {
  if (!field.state.meta.errors.length) return null;
  return (
    <p className="text-[11px] text-red-500 mt-1 font-medium">
      {joinFieldErrors(field.state.meta.errors)}
    </p>
  );
}

function fieldBorderClass(field: any) {
  return field.state.meta.errors.length
    ? "border-red-500 focus-visible:ring-red-500"
    : "";
}

export default function CtpPlateLogForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel = "Lưu",
}: CtpPlateLogFormProps) {
  const [customerId, setCustomerId] = useState<string>("");
  const form = useForm({
    defaultValues: {
      logDate: defaultValues?.logDate ?? toVnDateString(),
      productId: defaultValues?.productId ?? "",
      productName: defaultValues?.productName ?? "",
      poReference: defaultValues?.poReference ?? "",
      importQty: defaultValues?.importQty ?? 0,
      usedQty: defaultValues?.usedQty ?? 0,
      stockQty: defaultValues?.stockQty ?? 0,
      plateType: defaultValues?.plateType ?? "",
      notes: defaultValues?.notes ?? "",
    },
    validators: {
      onChange: ctpPlateLogFormSchema as unknown as undefined,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value as CtpPlateLogFormData);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 py-4">
        {/* Log Date */}
        <form.Field name="logDate">
          {(field) => (
            <div className="col-span-1 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Ngày (*)
              </Label>
              <Input
                type="date"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        {/* Plate Name/Type */}
        <form.Field name="plateType">
          {(field) => (
            <div className="col-span-1 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Tên Kẽm
              </Label>
              <Input
                placeholder="VD: Kẽm Kodak..."
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        {/* Customer Filter */}
        <div className="col-span-2 lg:col-span-1 space-y-1">
          <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
            Khách Hàng (Dùng để lọc sản phẩm)
          </Label>
          <SharedCombobox
            type="customer"
            label="Khách hàng"
            hideLabel={true}
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              // Clear product when customer changes
              form.setFieldValue("productId", "");
              form.setFieldValue("productName", "");
            }}
            placeholder="Chọn khách hàng để thu hẹp sản phẩm..."
          />
        </div>

        {/* Product FK */}
        <form.Field name="productId">
          {(field) => (
            <div className="col-span-2 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Sản Phẩm (*)
              </Label>
              <SharedCombobox
                type="product"
                params={{ customerId: customerId || undefined }}
                label="Sản Phẩm"
                hideLabel={true}
                value={field.state.value || ""}
                onChange={(id, opt) => {
                  field.handleChange(id);
                  if (opt) {
                    form.setFieldValue(
                      "productName",
                      opt.original?.name || opt.label,
                    );
                  }
                }}
                placeholder="Chọn sản phẩm..."
                error={field.state.meta.errors.length > 0}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        {/* PO Reference */}
        <form.Field name="poReference">
          {(field) => (
            <div className="col-span-2 lg:col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Số Kiện (PO)
              </Label>
              <Input
                placeholder="VD: PO-2026-001"
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        {/* Quantities */}
        <form.Field name="importQty">
          {(field) => (
            <div className="col-span-1 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                SL Nhập
              </Label>
              <NumericInput
                min={0}
                value={field.state.value}
                onChange={(v) => field.handleChange(Number(v) || 0)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        <form.Field name="usedQty">
          {(field) => (
            <div className="col-span-1 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                SL Sử Dụng
              </Label>
              <NumericInput
                min={0}
                value={field.state.value}
                onChange={(v) => field.handleChange(Number(v) || 0)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>

        <form.Field name="stockQty">
          {(field) => (
            <div className="col-span-1 lg:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Tồn Kho (Sau Giao Dịch)
              </Label>
              {/* Tồn do máy chủ tự tính theo khổ kẽm: nhập kẽm tăng, xuất kẽm CTP giảm (không nhập tay) */}
              <div
                className="h-9 px-3 flex items-center rounded-md border bg-slate-50 text-sm text-slate-600"
                data-testid="ctp-log-stock-readonly"
                title="Hệ thống tự tính theo khổ kẽm sau khi lưu"
              >
                {field.state.value || 0} · tự tính khi lưu
              </div>
            </div>
          )}
        </form.Field>

        {/* Notes */}
        <form.Field name="notes">
          {(field) => (
            <div className="col-span-2 lg:col-span-3 space-y-1">
              <Label className="text-xs font-semibold text-slate-600 whitespace-normal">
                Ghi Chú
              </Label>
              <Input
                placeholder="Ghi chú thêm..."
                value={field.state.value || ""}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className={cn("h-9 text-sm", fieldBorderClass(field))}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>
      </div>

      <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 backdrop-blur-md bg-white/80 p-4 border-t flex justify-end gap-3 mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Hủy
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {isSubmitting ? "Đang lưu..." : submitLabel}
            </Button>
          )}
        />
      </div>
    </form>
  );
}
